import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { User } from 'types/user';
import { formatDate } from 'utils/date';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  verifyEmail,
  verifyPhone,
  blockUserThunk,
  unblockUserThunk,
  setUserDetail,
} from 'store/usersSlice';
import { userService } from 'components/api/users';
import { showToast } from 'store/toastSlice';
import { Alert } from 'components/alert/alert';
import ConfirmModal from 'components/modals/ConfirmModal';
import DatePicker from 'components/common/datePicker/datePicker';
import styles from '../userDetails.module.scss';

interface UserInfoProps {
  user: User;
}

const UserInfo: React.FC<UserInfoProps> = ({ user }) => {
  const dispatch = useAppDispatch();
  const { isLoadingSmall } = useAppSelector((state) => state.users);

  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(
    null,
  );
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    type: 'email' | 'phone' | 'block' | 'unblock' | 'levelUp' | null;
  }>({
    open: false,
    type: null,
  });
  const [blockDurationType, setBlockDurationType] = useState<
    '2_HOURS' | '6_HOURS' | 'CUSTOM' | null
  >(null);
  const [customBlockDate, setCustomBlockDate] = useState<string>('');
  const [blockReason, setBlockReason] = useState<string>('');
  const [grantWheelSpin, setGrantWheelSpin] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showFeedback = useCallback((message: string, type: 'success' | 'error') => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setFeedback({ message, type });
    timeoutRef.current = setTimeout(() => {
      setFeedback(null);
      timeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleVerifyEmail = async () => {
    try {
      const resultAction = await dispatch(verifyEmail(user.id));
      if (!verifyEmail.fulfilled.match(resultAction)) {
        const message = (resultAction.payload as string) || 'Failed to verify email';
        showFeedback(message, 'error');
      }
    } catch (error: any) {
      showFeedback(error.message || 'An error occurred', 'error');
    }
  };

  const handleVerifyPhone = async () => {
    try {
      const resultAction = await dispatch(verifyPhone(user.id));
      if (!verifyPhone.fulfilled.match(resultAction)) {
        const message = (resultAction.payload as string) || 'Failed to verify phone';
        showFeedback(message, 'error');
      }
    } catch (error: any) {
      showFeedback(error.message || 'An error occurred', 'error');
    }
  };

  const handleToggleDeposit = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isDisabling = e.target.checked;
    const enabled = !isDisabling;
    try {
      await userService.toggleDeposit(user.id, enabled);
      dispatch(setUserDetail({ ...user, isDepositEnabled: enabled }));
      dispatch(
        showToast({
          text: `Deposits ${enabled ? 'enabled' : 'disabled'} successfully.`,
          type: 'success',
        }),
      );
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to toggle deposits';
      dispatch(showToast({ text: errorMsg, type: 'error' }));
    }
  };

  const handleToggleWithdraw = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isDisabling = e.target.checked;
    const enabled = !isDisabling;
    try {
      await userService.toggleWithdraw(user.id, enabled);
      dispatch(setUserDetail({ ...user, isWithdrawEnabled: enabled }));
      dispatch(
        showToast({
          text: `Withdrawals ${enabled ? 'enabled' : 'disabled'} successfully.`,
          type: 'success',
        }),
      );
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to toggle withdrawals';
      dispatch(showToast({ text: errorMsg, type: 'error' }));
    }
  };

  const handleToggleWithdrawManual = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isManual = e.target.checked;
    try {
      await userService.toggleWithdrawManual(user.id, isManual);
      dispatch(setUserDetail({ ...user, isWithdrawManual: isManual } as User));
      dispatch(
        showToast({
          text: `Manual withdrawal ${isManual ? 'enabled' : 'disabled'} successfully.`,
          type: 'success',
        }),
      );
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.message || err.message || 'Failed to toggle manual withdrawal';
      dispatch(showToast({ text: errorMsg, type: 'error' }));
    }
  };

  const handleOpenConfirm = (type: 'email' | 'phone' | 'block' | 'unblock' | 'levelUp') => {
    setConfirmModal({ open: true, type });
    setBlockDurationType(null);
    setCustomBlockDate('');
    setBlockReason('');
    setGrantWheelSpin(false);
  };

  const handleCloseConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, open: false }));

    // Clear type after animation completes to avoid content flash
    setTimeout(() => {
      setConfirmModal((prev) => (prev.open ? prev : { ...prev, type: null }));
    }, 300);
  };

  const getBlockDurationHours = () => {
    // 10 years in hours (10 * 365 * 24 = 87600)
    return 10 * 365 * 24;
    /*
    if (blockDurationType === '2_HOURS') return 2;
    if (blockDurationType === '6_HOURS') return 6;
    if (blockDurationType === 'CUSTOM' && customBlockDate) {
      const msDiff = new Date(customBlockDate).getTime() - Date.now();
      const hours = Math.ceil(msDiff / (1000 * 60 * 60));
      return hours > 0 ? hours : 1;
    }
    return 2;
    */
  };

  const handleBlockUser = async () => {
    const durationHours = getBlockDurationHours();
    try {
      const resultAction = await dispatch(
        blockUserThunk({ id: user.id, durationHours, reason: blockReason }),
      );
      if (blockUserThunk.fulfilled.match(resultAction)) {
        dispatch(
          setUserDetail({
            ...user,
            isBlocked: true,
          }),
        );
        dispatch(showToast({ text: 'User blocked successfully', type: 'success' }));
        handleCloseConfirm();
      } else {
        const message = (resultAction.payload as string) || 'Failed to block user';
        dispatch(showToast({ text: message, type: 'error' }));
      }
    } catch (error: any) {
      dispatch(showToast({ text: error.message || 'An error occurred', type: 'error' }));
    }
  };

  const handleUnblockUser = async () => {
    try {
      const resultAction = await dispatch(unblockUserThunk(user.id));
      if (unblockUserThunk.fulfilled.match(resultAction)) {
        dispatch(
          setUserDetail({
            ...user,
            isBlocked: false,
          }),
        );
        dispatch(showToast({ text: 'User unblocked successfully', type: 'success' }));
        handleCloseConfirm();
      } else {
        const message = (resultAction.payload as string) || 'Failed to unblock user';
        dispatch(showToast({ text: message, type: 'error' }));
      }
    } catch (error: any) {
      dispatch(showToast({ text: error.message || 'An error occurred', type: 'error' }));
    }
  };

  const handleConfirmAction = () => {
    if (confirmModal.type === 'email') {
      handleVerifyEmail();
    } else if (confirmModal.type === 'phone') {
      handleVerifyPhone();
    } else if (confirmModal.type === 'block') {
      handleBlockUser();
    } else if (confirmModal.type === 'unblock') {
      handleUnblockUser();
    } else if (confirmModal.type === 'levelUp') {
      handleLevelUp();
    }
    handleCloseConfirm();
  };

  const handleLevelUp = async () => {
    try {
      const response = await userService.levelUp(user.id, user.site.id, { grantWheelSpin });
      dispatch(showToast({ text: 'User level upgraded successfully', type: 'success' }));

      const newLevelKey = response.data?.newLevelKey;
      if (newLevelKey) {
        const [tier, levelNum] = newLevelKey.split('_');
        const tierName = tier.charAt(0).toUpperCase() + tier.slice(1);
        const name = `${tierName} ${levelNum || ''}`.trim();

        const updatedUser = {
          ...user,
          level: {
            ...user.level,
            key: newLevelKey,
            tier: tier,
            tierName: tierName,
            name: name,
          },
        };
        dispatch(setUserDetail(updatedUser as User));
      }
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to level up user',
          type: 'error',
        }),
      );
    }
  };

  const isConfirmDisabled = () => {
    if (confirmModal.type === 'block') {
      // if (!blockDurationType) return true;
      // if (blockDurationType === 'CUSTOM' && !customBlockDate) return true;
      if (!blockReason.trim()) return true;
    }
    return false;
  };

  const renderBlockForm = () => {
    if (confirmModal.type !== 'block') return null;

    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
          Select block duration:
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="blockDuration"
              value="2_HOURS"
              checked={blockDurationType === '2_HOURS'}
              onChange={() => setBlockDurationType('2_HOURS')}
            />
            2 Hours
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="blockDuration"
              value="6_HOURS"
              checked={blockDurationType === '6_HOURS'}
              onChange={() => setBlockDurationType('6_HOURS')}
            />
            6 Hours
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="blockDuration"
              value="CUSTOM"
              checked={blockDurationType === 'CUSTOM'}
              onChange={() => setBlockDurationType('CUSTOM')}
            />
            Custom Date
          </label>
        </Box>
        {blockDurationType === 'CUSTOM' && (
          <DatePicker
            label="Custom Date"
            value={customBlockDate}
            onChange={(val) => setCustomBlockDate(val)}
            disablePast
            useDialog
          />
        )} */}

        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
            Reason:
          </Typography>
          <input
            type="text"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="e.g., Suspicious withdrawal pattern"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              outline: 'none',
              backgroundColor: 'var(--bg-panel)',
              color: 'var(--text-primary)',
              fontSize: '14px',
            }}
          />
        </Box>
      </Box>
    );
  };

  if (isLoadingSmall) {
    return (
      <Box className={styles.loadingWrapper}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box className={styles.tabContent}>
      {feedback?.type === 'error' && <Alert message={feedback?.message} type="error" />}
      <section className={styles.infoCard}>
        <Typography variant="h6" className={styles.cardTitle}>
          User Details (ID: {user.id})
        </Typography>
        <ul className={styles.infoList}>
          <li className={styles.infoRow}>
            <span className={styles.label}>Username:</span>
            <span className={styles.value}>{(user as any).username || '-'}</span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Email:</span>
            <span className={styles.value}>
              {user.email}
              {(user as any).isEmailVerified ? (
                <span className={styles.verified}>(Verified)</span>
              ) : (
                <button className={styles.verifyBtn} onClick={() => handleOpenConfirm('email')}>
                  Verify
                </button>
              )}
            </span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Level:</span>
            <span className={styles.value}>
              {user.level ? `${user.level.tierName} (${user.level.name})` : '-'}
              <button
                className={styles.verifyBtn}
                onClick={() => handleOpenConfirm('levelUp')}
                style={{ marginLeft: '8px' }}
              >
                Level up
              </button>
            </span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>GoogleId:</span>
            <span className={styles.value}>{user.googleId || '-'}</span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Site:</span>
            <span className={styles.value}>{user?.site?.name || '-'}</span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Status:</span>
            <span className={styles.value}>
              <span
                style={{
                  color: !user.isBlocked ? 'var(--accent-green)' : 'var(--error-red)',
                  fontWeight: 600,
                }}
              >
                {user.isBlocked ? 'BLOCKED' : 'ACTIVE'}
              </span>
              {!user.isBlocked ? (
                <button
                  className={styles.verifyBtn}
                  onClick={() => handleOpenConfirm('block')}
                  style={{
                    borderColor: 'rgba(255, 77, 77, 0.4)',
                    backgroundColor: 'rgba(255, 77, 77, 0.05)',
                    color: '#ff4d4d',
                    marginLeft: '8px',
                  }}
                >
                  Block
                </button>
              ) : (
                <button
                  className={styles.verifyBtn}
                  onClick={() => handleOpenConfirm('unblock')}
                  style={{ marginLeft: '8px' }}
                >
                  Unblock
                </button>
              )}
            </span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Disable deposits:</span>
            <span className={styles.value} style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  color: user.isDepositEnabled === false ? '#f44336' : '#4caf50',
                  fontWeight: 600,
                  fontSize: '12px',
                }}
              >
                {user.isDepositEnabled === false ? 'RESTRICTED' : 'ALLOWED'}
              </Typography>
              <Switch
                checked={user.isDepositEnabled === false}
                onChange={handleToggleDeposit}
                size="small"
                color="error"
              />
            </span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Disable withdrawals:</span>
            <span className={styles.value} style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  color: !user.isWithdrawEnabled ? '#f44336' : '#4caf50',
                  fontWeight: 600,
                  fontSize: '12px',
                }}
              >
                {!user.isWithdrawEnabled ? 'RESTRICTED' : 'ALLOWED'}
              </Typography>
              <Switch
                checked={!user.isWithdrawEnabled}
                onChange={handleToggleWithdraw}
                size="small"
                color="error"
              />
            </span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Manual withdrawals:</span>
            <span className={styles.value} style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  color: (user as any).isWithdrawManual ? '#f44336' : '#4caf50',
                  fontWeight: 600,
                  fontSize: '12px',
                }}
              >
                {(user as any).isWithdrawManual ? 'MANUAL' : 'AUTO'}
              </Typography>
              <Switch
                checked={(user as any).isWithdrawManual || false}
                onChange={handleToggleWithdrawManual}
                size="small"
                color="error"
              />
            </span>
          </li>
          {user.balances?.map((balance, index) => (
            <React.Fragment key={index}>
              <li className={styles.infoRow} style={{ marginTop: '10px' }}>
                <span
                  className={styles.label}
                  style={{ color: 'var(--accent-blue)', fontWeight: 600 }}
                >
                  Balance ({balance.currency}):
                </span>
                <span className={styles.value}>{balance.balance}</span>
              </li>
              <li className={styles.infoRow}>
                <span className={styles.label}>Withdrawable:</span>
                <span className={styles.value}>{balance.withdrawable}</span>
              </li>
              <li className={styles.infoRow}>
                <span className={styles.label}>Vault:</span>
                <span className={styles.value}>{balance.vault}</span>
              </li>
              <li className={styles.infoRow}>
                <span className={styles.label}>Total wager:</span>
                <span className={styles.value}>{user.totalWager || '_'}</span>
              </li>
              {balance.vaultLockedUntil && (
                <li className={styles.infoRow}>
                  <span className={styles.label}>Vault Locked:</span>
                  <span className={styles.value}>{formatDate(balance.vaultLockedUntil)}</span>
                </li>
              )}
            </React.Fragment>
          ))}
          <li className={styles.infoRow} style={{ marginTop: '10px' }}>
            <span className={styles.label}>Created At:</span>
            <span className={styles.value}>{formatDate(user.createdAt)}</span>
          </li>
          <li className={styles.infoRow}>
            <span className={styles.label}>Last Login:</span>
            <span className={styles.value}>{formatDate(user.lastLoginAt)}</span>
          </li>
        </ul>
      </section>

      <ConfirmModal
        open={confirmModal.open}
        title={
          confirmModal.type === 'block'
            ? 'Confirm Block'
            : confirmModal.type === 'unblock'
              ? 'Confirm Unblock'
              : confirmModal.type === 'levelUp'
                ? 'Confirm Level Up'
                : 'Confirm Verification'
        }
        description={
          confirmModal.type === 'block'
            ? 'Are you sure you want to block this user?'
            : confirmModal.type === 'unblock'
              ? 'Are you sure you want to unblock this user?'
              : confirmModal.type === 'levelUp'
                ? 'Are you sure you want to manually level up this user? Please note that this action will advance the user by exactly one level.'
                : `Are you sure you want to verify this ${confirmModal.type}?`
        }
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmAction}
        confirmText={
          confirmModal.type === 'block'
            ? 'Block'
            : confirmModal.type === 'unblock'
              ? 'Unblock'
              : confirmModal.type === 'levelUp'
                ? 'Level up'
                : 'Verify'
        }
        confirmDisabled={isConfirmDisabled()}
      >
        {renderBlockForm()}
        {confirmModal.type === 'levelUp' && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={grantWheelSpin}
                  onChange={(e) => setGrantWheelSpin(e.target.checked)}
                  size="small"
                  sx={{
                    color: 'var(--text-secondary)',
                    '&.Mui-checked': {
                      color: 'var(--accent-blue) !important',
                    },
                  }}
                />
              }
              label="Grant wheel spin for this level"
              sx={{
                color: 'var(--text-primary)',
                userSelect: 'none',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        )}
      </ConfirmModal>
    </Box>
  );
};

export default UserInfo;
